import { useState } from "react";
import { useDataProvider, useGetIdentity, useNotify, useRecordContext, useRefresh } from "ra-core";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { automationActionTypes } from "../automations/automationModel";
import { runAutomations } from "../automations/runAutomations";
import type { AutomationJob, Prospect } from "../types";

interface PcmDesign {
  id: number;
  name: string;
}

/** PCM /design returns {results: [...], pagination} (confirmed via sandbox). */
function parseDesigns(data: unknown): PcmDesign[] {
  const obj = (data ?? {}) as Record<string, unknown>;
  const list = Array.isArray(data)
    ? data
    : Array.isArray(obj.results)
      ? (obj.results as unknown[])
      : Array.isArray(obj.designs)
        ? (obj.designs as unknown[])
        : [];
  return list
    .map((d) => {
      if (!d || typeof d !== "object") return null;
      const r = d as Record<string, unknown>;
      const id = Number(r.designID ?? r.id);
      if (!Number.isInteger(id) || id <= 0) return null;
      const name = String(r.name ?? r.friendlyName ?? `Design ${id}`);
      return { id, name };
    })
    .filter((d): d is PcmDesign => d !== null);
}

/**
 * Manual direct-mail send. Deliberately goes through the SAME outbox pipeline
 * as rule-fired automations (job row -> executor -> activities audit): the
 * confirm click here IS the approval, there is no second unsafe code path.
 */
export const ProspectSendMailButton = () => {
  const record = useRecordContext<Prospect>();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionType, setActionType] = useState("pcm.postcard");
  const [designIdText, setDesignIdText] = useState("");
  const [designs, setDesigns] = useState<PcmDesign[] | null>(null);
  const [loadingDesigns, setLoadingDesigns] = useState(false);

  if (!record || !identity) return null;

  const loadDesigns = async () => {
    setLoadingDesigns(true);
    try {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "pcm/api",
        { body: { action: "list_designs", payload: {} } },
      );
      if (error || !data?.success) {
        throw new Error(data?.error ?? error?.message ?? "list_designs failed");
      }
      const parsed = parseDesigns(data.data);
      setDesigns(parsed);
      if (!parsed.length) {
        notify("No designs found on the PostcardMania account", { type: "info" });
      }
    } catch (error: unknown) {
      notify(
        `Could not load designs (is PCM_API_TOKEN set?): ${
          error instanceof Error ? error.message : String(error)
        }`,
        { type: "warning" },
      );
    } finally {
      setLoadingDesigns(false);
    }
  };

  const send = async () => {
    const designID = Number(designIdText);
    if (!Number.isInteger(designID) || designID <= 0) {
      notify("Enter a valid PostcardMania design ID", { type: "warning" });
      return;
    }
    setBusy(true);
    try {
      const { data: job } = await dataProvider.create<AutomationJob>(
        "automation_jobs",
        {
          data: {
            sales_id: identity.id,
            prospect_id: record.id,
            rule_id: null,
            action_type: actionType,
            action_config: { designID },
            status: "approved",
            approved_at: new Date().toISOString(),
          },
        },
      );
      const run = await runAutomations([Number(job.id)]);
      const outcome = run.results.find((r) => r.id === Number(job.id));
      if (!outcome) {
        notify(
          "Job was picked up by another run — check the Automations outbox for its status",
          { type: "info" },
        );
        setOpen(false);
        refresh();
      } else if (outcome.status === "sent") {
        notify(
          outcome.auditLogged
            ? "Order placed — logged on the timeline"
            : "Order placed (timeline logging failed — see the Automations outbox)",
          { type: "success" },
        );
        setOpen(false);
        refresh();
      } else {
        notify(
          `Send failed: ${outcome.error ?? "unknown"} — the job is in the Automations outbox for retry`,
          { type: "warning" },
        );
      }
    } catch (error: unknown) {
      notify(
        `Could not send: ${error instanceof Error ? error.message : String(error)}`,
        { type: "warning" },
      );
    } finally {
      setBusy(false);
    }
  };

  const kindLabel =
    automationActionTypes.find((a) => a.id === actionType)?.name ?? actionType;
  const mailTo =
    record.mailing_address || record.property_address || "(no address on file)";

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        title="Mail a postcard or letter to this owner via PostcardMania"
      >
        <Mail className="size-4" /> Send mail
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send direct mail</DialogTitle>
            <DialogDescription>
              To {record.owner_name} at {mailTo}. Placing the order spends real
              money and mails a physical piece.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Label>Type</Label>
              <select
                className="h-9 rounded-md border bg-transparent px-2 text-sm"
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
              >
                {automationActionTypes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label>Design</Label>
              {designs && designs.length > 0 ? (
                <select
                  className="h-9 rounded-md border bg-transparent px-2 text-sm"
                  value={designIdText}
                  onChange={(e) => setDesignIdText(e.target.value)}
                >
                  <option value="">Pick a design…</option>
                  {designs.map((d) => (
                    <option key={d.id} value={String(d.id)}>
                      {d.name} (#{d.id})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Design ID"
                    value={designIdText}
                    onChange={(e) => setDesignIdText(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void loadDesigns()}
                    disabled={loadingDesigns}
                  >
                    {loadingDesigns ? "Loading…" : "Load designs"}
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="button" onClick={() => void send()} disabled={busy}>
              {busy ? "Placing order…" : `Send ${kindLabel.split(" ")[0].toLowerCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
